import { AxiosError } from 'axios';
import BaseTemplate from '../../components/baseTemplate';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import { isBadRequestResponse } from '../../../../utils/helpers';
import { IUser } from '../../../../utils/interfaces/user';
import { LoginService } from '../../../../services/login.service';
import MfaCodesInputs from '../../components/mfaCodesInputs';
import { qrValidations } from './validations';
import Spinner from '../../../../components/ui/spinner';
import { useFormik } from 'formik';
import { USER_ACTIONS } from '../../../../utils/enums/user-store-actions.enum';
import { useTranslation } from 'react-i18next';
import {
    ACCESS_USER_NAME,
    ASSOCIATIONS_NAME,
    COGNITO_TOKEN_NAME,
    DEALER_COUNTRY_NAME,
    DEFAULTDEALER_NAME,
    DISTRICTS_NAME,
    FULL_NAME,
    PARTYID_NAME,
    PERMISSIONS_NAME,
    PERMISSIONS_TOKEN_NAME,
    USER_NAME,
} from '../../../../config';
import {
    LABEL_FIELD_ERROR_INVALID_OR_EXPIRED_CODE,
    LABEL_FIELD_ERROR_REQUEST_SERVICE,
    LABEL_TEXT_ERROR_ATTEMPT_SUBTITLE,
    LABEL_TEXT_ERROR_ATTEMPT_TITLE,
    LABEL_TEXT_VERIFICATION_REMEMBER_ME,
    LABEL_TEXT_VERIFICATION_SUBMIT,
    LABEL_TEXT_VERIFICATION_SUBTITLE,
    LABEL_TITLE_VERIFICATION,
} from '../../../../localization/resouceKeys';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './styles.scss';

async function rememberDevice(username: string, deviceChecked: boolean) {
    if (deviceChecked) {
        const deviceService = new LoginService();
        try {
            await deviceService.registerDevice(username);
        } catch (e) {
            console.log(e);
        }
    }
}

const MfaCode = () => {
    const { t } = useTranslation();
    const MAX_ATTEMPTS = 5;
    const INITIAL_VALUE = 0;
    const [loading, setLoading] = useState<boolean>(false);
    const [invalidTokenError, setInvalidTokenError] = useState<boolean>(false);
    const [invalidServiceError, setInvalidServiceError] =
        useState<boolean>(false);
    const [deviceChecked, setChecked] = useState<boolean>(false);
    const [attempts, increaseAttempts] = useState<number>(INITIAL_VALUE);
    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
        setFieldTouched,
    } = useFormik({
        initialValues: {
            code1: '',
            code2: '',
            code3: '',
            code4: '',
            code5: '',
            code6: '',
        },
        validationSchema: qrValidations,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: handlerSubmit,
    });
    const memoizedHandleChange = useCallback(handleChange, []);
    const memoizedHandleBlur = useCallback(handleBlur, []);
    const memoizedSetFieldValue = useCallback(setFieldValue, []);
    const memoizedSetFieldTouched = useCallback(setFieldTouched, []);
    const userDispatch = useDispatch();
    const username = useSelector((state: IUser) => {
        return state.username;
    });
    const password = useSelector((state: IUser) => {
        return state.password;
    });

    function handleCountIncrease(count: number) {
        if (count) {
            increaseAttempts(count);
        }
    }
    async function handlerSubmit() {
        setLoading(true);
        setInvalidTokenError(false);
        setInvalidServiceError(false);
        const loginService = new LoginService();
        try {
            const resp = await loginService.sendAuthCode(
                username,
                `${Object.entries(values)
                    .map((e) => e[1])
                    .toString()
                    .replaceAll(',', '')}`,
                password,
            );
            let fullName = '';
            if (resp.data.statusCode == HttpStatusCode.LOCKED) {
                userDispatch({
                    type: USER_ACTIONS.SET_BLOCKED,
                    payload: true,
                });
            } else {
                sessionStorage.setItem(
                    COGNITO_TOKEN_NAME,
                    resp.data.cognitoToken,
                );
                sessionStorage.setItem(USER_NAME, username);
                const request_profile = await loginService.getDealerProfile(
                    resp.data.permissionsToken,
                );
                if (
                    request_profile &&
                    request_profile.status === HttpStatusCode.SUCCESS
                ) {
                    const {
                        dsourceProfile,
                        userProfile,
                        userPermissions,
                        associations,
                        districts,
                    } = request_profile.data.userProfile.userData;
                    const { countryCode } =
                        dsourceProfile?.user?.phoneNumbers?.[0] ?? {};
                    fullName = `${userProfile.userFirstName} ${userProfile.userLastName}`;
                    sessionStorage.setItem(
                        DEALER_COUNTRY_NAME,
                        countryCode || 'NA',
                    );
                    sessionStorage.setItem(
                        PERMISSIONS_TOKEN_NAME,
                        resp.data.permissionsToken,
                    );
                    sessionStorage.setItem(FULL_NAME, fullName);
                    sessionStorage.setItem(
                        DEFAULTDEALER_NAME,
                        userProfile.userDefaultDealer,
                    );
                    sessionStorage.setItem(
                        PERMISSIONS_NAME,
                        JSON.stringify(userPermissions),
                    );
                    sessionStorage.setItem(
                        ASSOCIATIONS_NAME,
                        JSON.stringify(associations),
                    );
                    rememberDevice(username, deviceChecked);
                    sessionStorage.setItem(
                        ACCESS_USER_NAME,
                        JSON.stringify(userProfile.userIsFieldAccessUser),
                    );
                    sessionStorage.setItem(
                        PARTYID_NAME,
                        JSON.stringify(userProfile.userPartyId),
                    );
                    if (userProfile.userIsFieldAccessUser === true) {
                        sessionStorage.setItem(
                            DISTRICTS_NAME,
                            JSON.stringify(districts),
                        );
                    }
                    userDispatch({
                        type: USER_ACTIONS.MFA_TOKEN_VALID,
                        payload: {
                            fullName,
                            cognitoToken: resp.data.permissionsToken,
                            permissionsToken: resp.data.permissionsToken,
                            dealerCountry: countryCode || 'NA',
                            accessUser: userProfile.userIsFieldAccessUser,
                            districs: districts || [],
                            partyId: userProfile.userPartyId,
                        },
                    });
                } else {
                    throw Error();
                }
            }
        } catch (error) {
            setLoading(false);
            setInvalidServiceError(true);
            const err = error as AxiosError;
            if (isBadRequestResponse(err)) {
                const data: any = err.response?.data;
                const count = data.error.params.loginAttempCount;
                handleCountIncrease(count);
                setInvalidTokenError(true);
                setInvalidServiceError(false);
            }
        }
        setLoading(false);
    }
    const [isDisabled, setIsDisabled] = useState(true);
    useEffect(() => {
        if (
            values.code1 === '' ||
            values.code2 === '' ||
            values.code3 === '' ||
            values.code4 === '' ||
            values.code5 === '' ||
            values.code6 === '' ||
            Object.keys(errors).length ||
            loading
        )
            setIsDisabled(true);
        else setIsDisabled(false);
    }, [
        loading,
        values.code1,
        values.code2,
        values.code3,
        values.code4,
        values.code5,
        values.code6,
        Object.keys(errors).length,
    ]);
    return (
        <BaseTemplate>
            <div className={'form-wrapper'}>
                <div className={'mfa-form'}>
                    <div className={'header u-mb-xxsmall'}>
                        <h4>{t(LABEL_TITLE_VERIFICATION)}</h4>
                        <p>{t(LABEL_TEXT_VERIFICATION_SUBTITLE)}</p>
                    </div>
                    {attempts > INITIAL_VALUE && (
                        <div className={'attempt-section'}>
                            <strong>
                                {t(LABEL_TEXT_ERROR_ATTEMPT_TITLE, {
                                    numberAttempts: MAX_ATTEMPTS - attempts,
                                })}
                            </strong>
                            <p>
                                {t(LABEL_TEXT_ERROR_ATTEMPT_SUBTITLE, {
                                    maxAttempts: MAX_ATTEMPTS,
                                })}
                            </p>
                        </div>
                    )}
                    <form
                        onSubmit={handleSubmit}
                        autoComplete={'off'}
                        className={'u-mt-xxsmall u-mb-small'}
                    >
                        <MfaCodesInputs
                            handleBlur={memoizedHandleBlur}
                            handleChange={memoizedHandleChange}
                            values={values}
                            touched={touched}
                            setFieldValue={memoizedSetFieldValue}
                            setFieldTouched={memoizedSetFieldTouched}
                        />
                        {(invalidTokenError || invalidServiceError) && (
                            <span
                                className={'c-error-message'}
                                style={{ position: 'absolute' }}
                            >
                                {invalidTokenError
                                    ? t(
                                          LABEL_FIELD_ERROR_INVALID_OR_EXPIRED_CODE,
                                      )
                                    : t(LABEL_FIELD_ERROR_REQUEST_SERVICE)}
                            </span>
                        )}
                        <div className={'u-mt'}>
                            <label className='c-checkbox'>
                                <input
                                    className='c-checkbox__input  '
                                    type='checkbox'
                                    name='checkbox'
                                    defaultChecked={deviceChecked}
                                    onChange={() => setChecked(!deviceChecked)}
                                />
                                <span className='c-checkbox__label'>
                                    {t(LABEL_TEXT_VERIFICATION_REMEMBER_ME)}
                                </span>
                            </label>
                        </div>
                        <div className='sg-component-variant u-mt-large'>
                            <button
                                className='main-btn c-btn c-btn--full'
                                disabled={isDisabled}
                                type={'submit'}
                            >
                                <span className='c-btn__text'>
                                    {loading ? (
                                        <Spinner />
                                    ) : (
                                        t(LABEL_TEXT_VERIFICATION_SUBMIT)
                                    )}
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </BaseTemplate>
    );
};

export default MfaCode;
